/*
 * Загрузка снимка парсера + «Обновить». «Обновить» шлёт идемпотентную команду
 * REFRESH_PARSER (движок пере-собирает кандидатов даже при выключенном боте — §Этап 9)
 * и затем перечитывает /parser. Фоновый рефетч держит «Обновлено» свежим.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, getParser, newCommandId, postCommand } from '@/lib/api'
import { CommandKind, type ParserSnapshot } from '@/lib/types'
import { useAppStore } from '@/store/useAppStore'

const BACKGROUND_REFETCH_MS = 20_000
/** Пауза после REFRESH_PARSER перед перечитыванием (движок обрабатывает команду async). */
const REFRESH_SETTLE_MS = 600

interface ParserHook {
  snapshot: ParserSnapshot | null
  loading: boolean
  refreshing: boolean
  error: string | null
  refresh: () => void
}

export function useParser(): ParserHook {
  const [snapshot, setSnapshot] = useState<ParserSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mounted = useRef(true)
  // Монотонный id запроса: применяем только самый свежий ответ (фоновый рефетч и
  // ручной «Обновить» могут лететь параллельно — не даём устаревшему перетереть).
  const reqSeq = useRef(0)

  const handleError = useCallback((err: unknown) => {
    if (err instanceof DOMException && err.name === 'AbortError') return
    if (err instanceof ApiError) {
      if (err.status === 401) {
        useAppStore.getState().logout()
        return
      }
      if (mounted.current) setError(err.detail)
      return
    }
    if (mounted.current) setError('Не удалось загрузить парсер')
  }, [])

  const load = useCallback(
    async (signal?: AbortSignal) => {
      const seq = ++reqSeq.current
      try {
        const snap = await getParser(signal)
        // Применяем, только если это всё ещё самый свежий запрос (анти-clobber).
        if (mounted.current && seq === reqSeq.current) {
          setSnapshot(snap)
          setError(null)
        }
      } catch (err) {
        if (seq === reqSeq.current) handleError(err)
      }
    },
    [handleError],
  )

  // Первичная загрузка + фоновый рефетч (пауза при скрытой вкладке).
  useEffect(() => {
    mounted.current = true
    const ctrl = new AbortController()
    setLoading(true)
    void load(ctrl.signal).finally(() => {
      if (mounted.current) setLoading(false)
    })

    const id = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, BACKGROUND_REFETCH_MS)

    return () => {
      mounted.current = false
      ctrl.abort()
      clearInterval(id)
    }
  }, [load])

  const refresh = useCallback(() => {
    if (!mounted.current) return
    setRefreshing(true)
    setError(null)
    void (async () => {
      try {
        await postCommand(newCommandId('refresh'), CommandKind.REFRESH_PARSER)
        await new Promise((r) => setTimeout(r, REFRESH_SETTLE_MS))
        await load()
      } catch (err) {
        handleError(err)
      } finally {
        if (mounted.current) setRefreshing(false)
      }
    })()
  }, [load, handleError])

  return { snapshot, loading, refreshing, error, refresh }
}
